/**
 * Parse the API response and return the data json or error message
 */

import { ApiFetchStatus, AppDetailsFetchErrorType } from "@tdp/common/constants";
import { ILogger } from "@tdp/common/telemetry";
import { ZodTypeAny, z } from "zod";

interface IApiResponseParser<T extends ZodTypeAny> {
  response: Response;
  logger: ILogger;
  errorMessage: string;
  zodType: T
}

export async function parseApiResponse<T extends ZodTypeAny>(
  { response, logger, zodType, errorMessage }: IApiResponseParser<T>
) {
  if (response.status == 200 || response.status == 201 || response.status == 202) {
    const responseObject = (await response.json()) as unknown;
    const parsedResponseObject = zodType.safeParse(responseObject);
    if (!parsedResponseObject.success) {
      logger.error(`Parsing Error | Message: ${errorMessage}`);
      for (const error of parsedResponseObject.error.errors) {
        logger.error('\t[%s]: %s', error.path.join('.'), error.message);
      }
      return {
        type: ApiFetchStatus.ERROR,
        errorType: AppDetailsFetchErrorType.PARSING_ERROR,
        errorMessage: parsedResponseObject?.error.toString() ?? parsedResponseObject.error.errors
          .map((e) => e.message)
          .join(', '),
      };
    }
    return {
      type: ApiFetchStatus.SUCCESS,
      data: parsedResponseObject.data as z.infer<T>,
    };
  }
  else {
    logger.error(`Network Error | Reason: ${response.statusText} | Message: ${errorMessage}`);
    const responseObject = (await response.json());
    return {
      type: ApiFetchStatus.ERROR,
      errorType: AppDetailsFetchErrorType.NETWORK_ERROR,
      errorMessage: responseObject?.errorMessage ?? responseObject?.errorCode ?? response.statusText,
    };
  }

}
